import { useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export function usePlaidStart(onLinked) {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const { open, ready } = usePlaidLink({
    token,
    onSuccess: async (publicToken, metadata) => {
      setBusy(true);
      try {
        const r = await api.post("/plaid/exchange", { public_token: publicToken, institution_name: metadata?.institution?.name });
        toast.success(`Linked. ${r.data.sync?.holdings_imported ?? 0} holdings imported.`);
        onLinked?.();
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setBusy(false);
        setToken(null);
      }
    },
    onExit: (err) => {
      if (err?.error_code === "MFA_NOT_SUPPORTED") {
        toast.error("Plaid can't use the verification method on this account yet. Set TSP.gov to send codes by text, or use \"Update TSP\" → Option 2 to type shares or upload your statement.", { duration: 12000 });
      } else if (err) toast.error(err.display_message || err.error_message || "Plaid Link closed");
      setToken(null);
    },
  });
  useEffect(() => { if (token && ready) open(); }, [token, ready, open]);
  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/plaid/link-token");
      setToken(r.data.link_token);
    } catch (e) {
      toast.error(`Plaid: ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return { start, busy };
}

export const PLAID_START_EVENT = "pw:plaid-start";

export function PlaidLauncher({ onLinked }) {
  const { start } = usePlaidStart(onLinked);
  useEffect(() => {
    const f = () => start();
    window.addEventListener(PLAID_START_EVENT, f);
    return () => window.removeEventListener(PLAID_START_EVENT, f);
  }, [start]);
  return null;
}

export function PlaidConnect({ label = "Connect TSP / IRA via Plaid", onLinked }) {
  const { start, busy } = usePlaidStart(onLinked);
  return (
    <Button variant="outline" className="rounded-full gap-2" onClick={start} disabled={busy} data-testid="plaid-connect-button">
      <LinkSimple size={16} /> {busy ? "Connecting..." : label}
    </Button>
  );
}
